import { Box, Typography } from '@mui/material';
import {
  CircleAlert,
  CircleCheck,
  CircleX,
  Database,
  GitBranch,
  HardDrive,
  Inbox,
  Server,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import type { ReactElement } from 'react';

import type { AdminHealth, AdminHealthCheck, AdminMetrics } from '@/admin/adminApi';
import { Button, ErrorState, Icon, Skeleton } from '@/ui/kit';
import type { ColorTokens } from '@/ui/tokens';

import { apiValue, checkLabel, checkValue, clock, compactHealthSegments, entryPath, HEALTH_VERDICTS } from './dashboardModel';
import type { AdminRead } from './useAdminRead';

/**
 * Bandeau de santé : le verdict, puis une pastille par verification renvoyee.
 *
 * Quatre regles :
 * - **une erreur sur `/admin/health` n'est jamais un bandeau vert** : « Impossible
 *   d'interroger l'état du service ». Ne pas savoir n'est pas aller bien ;
 * - **une pastille par verification renvoyee**, `failed_transport` comprise ; une
 *   verification inconnue garde son nom ;
 * - **« Vérifié à HH:MM:SS »** est une heure et non un compteur : l'ecran ne se
 *   relit pas seul (ADR 0035 §4), l'heure est ce qui dit si la page est fraiche ;
 * - **ce que le serveur ne suit pas est dit** : la derniere sauvegarde est « non
 *   suivie » (neutre) tant que le suivi n'existe pas (BO-23), jamais « OK ».
 *
 * Sous 600 px, le bandeau tient en une ligne de texte (verdict, API, file,
 * migrations) — composition de l'astreinte, ou l'on cherche d'abord « ca
 * marche-t-il ? ».
 */

type Status = AdminHealth['status'];

const STATUS_STYLE: Readonly<
  Record<Status, { readonly fill: keyof ColorTokens; readonly ink: keyof ColorTokens; readonly icon: LucideIcon }>
> = {
  ok: { fill: 'successSoft', ink: 'successText', icon: CircleCheck },
  degraded: { fill: 'accentYellowSoft', ink: 'accentYellowStrong', icon: TriangleAlert },
  failed: { fill: 'criticalSoft', ink: 'criticalText', icon: CircleX },
};

const CHECK_ICONS: Readonly<Record<string, LucideIcon>> = {
  database: Database,
  queue: Inbox,
  migrations: GitBranch,
};

const Pill = ({
  icon,
  label,
  value,
  status,
}: {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly value: string;
  readonly status: Status | 'neutral';
}): ReactElement => {
  const style = status === 'neutral' ? { fill: 'neutralSoft', ink: 'textMuted' } : STATUS_STYLE[status];

  return (
    <Box
      component="li"
      sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.25, borderRadius: '12px', bgcolor: 'tokens.surface', border: 1, borderColor: 'tokens.border', minWidth: 0 }}
    >
      <Box
        aria-hidden
        sx={{ display: 'inline-flex', p: 0.75, borderRadius: '8px', flexShrink: 0, bgcolor: `tokens.${style.fill}`, color: `tokens.${style.ink}` }}
      >
        <Icon icon={icon} size={16} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="p" sx={{ fontSize: 12, lineHeight: '16px', color: 'tokens.textMuted' }}>
          {label}
        </Typography>
        <Typography component="p" sx={{ fontSize: 14, fontWeight: 700, lineHeight: '20px', overflowWrap: 'anywhere' }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const CheckPill = ({ check }: { readonly check: AdminHealthCheck }): ReactElement => (
  <Pill
    icon={CHECK_ICONS[check.name] ?? CircleAlert}
    label={checkLabel(check.name)}
    value={checkValue(check)}
    status={check.status}
  />
);

const apiPill = (metrics: AdminRead<AdminMetrics>): ReactElement => (
  <Pill
    icon={Server}
    label="API · médiane / p95"
    // « — » veut dire « rien mesure » ; une lecture impossible se dit autrement.
    value={metrics.kind === 'ready' ? apiValue(metrics.data.api.p50Ms, metrics.data.api.p95Ms) : metrics.kind === 'loading' ? '…' : 'Indisponible'}
    status="neutral"
  />
);

const Verdict = ({ status, narrow }: { readonly status: Status; readonly narrow: boolean }): ReactElement => {
  const { ink, icon } = STATUS_STYLE[status];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, color: `tokens.${ink}`, minWidth: 0 }}>
      <Icon icon={icon} size={narrow ? 20 : 22} />
      <Typography component="p" sx={{ fontSize: narrow ? 14 : 15, fontWeight: 800, lineHeight: '20px' }}>
        {HEALTH_VERDICTS[status]}
      </Typography>
    </Box>
  );
};

export const HealthBand = ({
  health,
  metrics,
  narrow,
  onRetry,
}: {
  readonly health: AdminRead<AdminHealth>;
  readonly metrics: AdminRead<AdminMetrics>;
  readonly narrow: boolean;
  readonly onRetry: () => void;
}): ReactElement => {
  if (health.kind === 'loading') {
    return (
      <Box role="status" aria-label="Chargement : état du service">
        <Skeleton height={narrow ? 64 : 150} radius={16} />
      </Box>
    );
  }

  if (health.kind === 'forbidden') {
    return (
      <ErrorState
        title="Impossible d’interroger l’état du service"
        message="Votre compte est connecté, mais son rôle ne permet pas de lire l’état du service."
        onRetry={onRetry}
      />
    );
  }

  if (health.kind === 'error') {
    return (
      <ErrorState
        title="Impossible d’interroger l’état du service"
        message={`${health.message} Dernière tentative à ${clock(health.at)}.`}
        onRetry={onRetry}
      />
    );
  }

  const { data, at } = health;
  const style = STATUS_STYLE[data.status];
  const detailLink = entryPath('health');
  const checked = (
    <Typography component="p" sx={{ fontSize: 13, lineHeight: '20px', color: `tokens.${style.ink}` }}>
      {`Vérifié à ${clock(at)}`}
    </Typography>
  );

  if (narrow) {
    const p50 = metrics.kind === 'ready' ? metrics.data.api.p50Ms : null;

    return (
      <Box component="section" aria-label="État du service" sx={{ p: 1.75, borderRadius: '14px', bgcolor: `tokens.${style.fill}`, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Verdict status={data.status} narrow />
        <Typography component="p" sx={{ fontSize: 12, lineHeight: '16px', color: `tokens.${style.ink}` }}>
          {compactHealthSegments(data, p50).join(' · ')}
        </Typography>
        {checked}
      </Box>
    );
  }

  return (
    <Box
      component="section"
      aria-label="État du service"
      sx={{ p: 2, borderRadius: '16px', bgcolor: `tokens.${style.fill}`, display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Verdict status={data.status} narrow={false} />
        {checked}
        <Box sx={{ flex: 1 }} />
        {detailLink === null ? null : (
          <Button variant="ghost" size="S" to={detailLink}>
            Santé du service
          </Button>
        )}
      </Box>
      <Box
        component="ul"
        sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1.25, gridTemplateColumns: 'repeat(auto-fit, minmax(165px, 1fr))' }}
      >
        {apiPill(metrics)}
        {data.checks.map((check) => (
          <CheckPill key={check.name} check={check} />
        ))}
        <Pill icon={HardDrive} label="Dernière sauvegarde" value="Non suivie" status="neutral" />
      </Box>
    </Box>
  );
};
